// The sign tools beside a PDF (PDFViewer's `sidePanel`), from the pdf-web-app
// signing panel trimmed to one signer and one signature in a fixed place: the
// signature made by drawing it (pressure-like strokes, thicker where the hand
// slows), or typing the name in a script hand (or on a phone, `mobile`); in
// black or blue ink; seen before it is used, with Change and Clear. Then the
// signer's full name (first and last, letters only), the consent
// ticked, and Sign — which hands the host
//
//   onSign({ image: "data:image/png;base64,…", name })
//
// the image a PNG with a transparent background, cut to the ink, at most
// 900 × 300 and under 300 KB as a data url (./signature.js). Where it goes on
// the document is the host's (a server stamps it into its signature block).
//
//   <SignTools onSign={sign} busy={signing} error={message} defaultName={user.name} consentText={t`I have read …`} texts={{ sign: t`Sign the contract`, … }} />
//
// With `mobile`, a fourth way — sign with your phone (not shown on a phone's
// width, where one just draws): a QR code of a page the phone draws the
// signature on, polled until it arrives, then seen like any other. The host
// brings the backend and the QR code (aldoo-ra carries no QR library):
//
//   mobile={{
//     createSession: async () => ({ token, expiresAt }),
//     getSession: async (token) => ({ status: "waiting" | "signed", image, expired }),
//     urlOf: (token) => "https://…/sign-on-phone?session=…",
//     renderQR: (url) => <QRCode value={url} />,
//   }}
import React, { useEffect, useRef, useState } from "react"
import { personNameError } from "aldoo-ra/Store/address-validation"
import { twMerge } from "tailwind-merge"
import { motion, AnimatePresence } from "framer-motion"
import { PencilIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import { signaturePng } from "./signature"
import DrawPad from "./DrawPad"

export const TEXTS = {
  title: "Sign the document",
  signature: "Your signature",
  draw: "Draw",
  type: "Type",
  color: "Color",
  black: "Black",
  blue: "Blue",
  drawHere: "Draw your signature here",
  typeHere: "Type your name",
  clear: "Clear",
  change: "Change",
  useSignature: "Use this signature",
  name: "Your full name",
  nameShort: "Enter your first and last name, in letters only",
  consent: "I have read the document and I sign it",
  sign: "Sign",
  signing: "Signing…",
  phone: "Phone",
  phoneTitle: "Sign with your phone",
  phoneHint: "Scan the code with your phone's camera, sign on its screen, and the signature appears here.",
  phoneLink: "Or open this link on your phone:",
  copyLink: "Copy link",
  copied: "Copied",
  phoneWaiting: "Waiting for the signature from your phone…",
  // %s: the time the code is good until
  phoneValidUntil: "The code is valid until %s",
  phoneExpired: "The code has expired",
  phoneError: "The code could not be made",
  newCode: "New code"
}

const COLORS = [
  { id: "black", value: "#000000" },
  { id: "blue", value: "#0000ff" }
]

// script hands with Cyrillic as well as Latin letters, from Google Fonts
const FONTS = [
  { family: "Great Vibes", weight: 400 },
  { family: "Caveat", weight: 600 },
  { family: "Marck Script", weight: 400 },
  { family: "Bad Script", weight: 400 }
]
const FONTS_CSS = "https://fonts.googleapis.com/css2?family=Great+Vibes&family=Caveat:wght@600&family=Marck+Script&family=Bad+Script&display=swap"

let fontsLinked = false
function linkFonts() {
  if (fontsLinked || typeof document === "undefined") return
  fontsLinked = true
  const link = document.createElement("link")
  link.rel = "stylesheet"
  link.href = FONTS_CSS
  document.head.appendChild(link)
}

const inputClass = "w-full h-[38px] px-3 rounded-md border border-admin_bordered bg-admin_paper text-admin_text text-sm outline-none focus:border-admin_primary disabled:opacity-60"

const Label = ({ children }) => <span className="text-xs font-medium text-admin_text_secondary">{children}</span>

// ---- Type ----

/** The name in a script hand on a transparent canvas, as large as fits. */
async function typedCanvas(text, font, color) {
  const spec = (size) => `${font.weight} ${size}px "${font.family}"`
  await document.fonts?.load(spec(120), text).catch(() => null)
  const canvas = document.createElement("canvas")
  canvas.width = 1600
  canvas.height = 400
  const ctx = canvas.getContext("2d", { willReadFrequently: true })
  let size = 160
  ctx.font = spec(size)
  while (size > 30 && ctx.measureText(text).width > canvas.width - 80) {
    size -= 8
    ctx.font = spec(size)
  }
  ctx.fillStyle = color
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(text, canvas.width / 2, canvas.height / 2)
  return canvas
}

function TypePad({ color, defaultText, onDraft, texts }) {
  const [text, setText] = useState(defaultText || "")
  const [font, setFont] = useState(FONTS[0])

  useEffect(linkFonts, [])

  useEffect(() => {
    const value = text.trim()
    if (!value) return onDraft(null)
    let gone = false
    typedCanvas(value, font, color).then((canvas) => !gone && onDraft(canvas))
    return () => {
      gone = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, font, color])

  return (
    <div className="flex flex-col gap-2" data-testid="sign-type">
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder={texts.typeHere} className={inputClass} maxLength={60} data-testid="sign-typed" />
      <div className="grid grid-cols-2 gap-2">
        {FONTS.map((f) => (
          <button
            key={f.family}
            type="button"
            onClick={() => setFont(f)}
            className={twMerge(
              "h-14 px-2 rounded-md border bg-white overflow-hidden whitespace-nowrap text-ellipsis transition-colors",
              f === font ? "border-admin_primary ring-1 ring-admin_primary" : "border-admin_bordered hover:border-admin_primary"
            )}
            style={{ fontFamily: `"${f.family}", cursive`, fontWeight: f.weight, fontSize: 24, color }}
          >
            {text.trim() || texts.typeHere}
          </button>
        ))}
      </div>
    </div>
  )
}

// ---- Phone ----

const POLL_MS = 2000

const timeOf = (date) => {
  try {
    return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  } catch {
    return ""
  }
}

/** A QR code of the phone's page for a new session; the session polled until the phone's signature is in (onImage). */
function PhonePad({ mobile, onImage, texts }) {
  const [session, setSession] = useState(null)
  const [state, setState] = useState("loading")
  const [copied, setCopied] = useState(false)
  // bumped by New code: a new session
  const [round, setRound] = useState(0)
  const handed = useRef(onImage)
  handed.current = onImage

  // a new session, each round
  useEffect(() => {
    let gone = false
    setSession(null)
    setState("loading")
    Promise.resolve()
      .then(() => mobile.createSession())
      .then((res) => {
        if (gone) return
        if (!res?.token) return setState("error")
        setSession(res)
        setState("waiting")
      })
      .catch(() => !gone && setState("error"))
    return () => {
      gone = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round])

  // polled while it waits (the pad mounted: the Phone tab open)
  useEffect(() => {
    if (state !== "waiting" || !session?.token) return
    let busy = false
    const timer = setInterval(async () => {
      if (busy) return
      if (session.expiresAt && Date.now() > new Date(session.expiresAt).getTime()) return setState("expired")
      busy = true
      const res = await Promise.resolve()
        .then(() => mobile.getSession(session.token))
        .catch(() => null)
      busy = false
      if (!res) return
      if (res.expired || res.error === "session_expired") return setState("expired")
      if (res.status !== "signed" || !res.image) return
      setState("signed")
      handed.current?.(res.image)
    }, POLL_MS)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, session])

  const url = session?.token ? mobile.urlOf(session.token) : ""

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }

  const again = (
    <Button variant="admin_secondary" className="w-full h-[34px]" onClick={() => setRound((n) => n + 1)} data-testid="sign-phone-new">
      {texts.newCode}
    </Button>
  )

  if (state === "loading")
    return (
      <div className="flex items-center justify-center rounded-md border border-dashed border-admin_bordered bg-white" style={{ aspectRatio: "2 / 1" }} data-testid="sign-phone">
        <div className="w-6 h-6 rounded-full border-2 border-admin_primary border-t-transparent animate-spin" />
      </div>
    )
  if (state === "error" || state === "expired")
    return (
      <div className="flex flex-col gap-2" data-testid="sign-phone">
        <div className="rounded-md border border-dashed border-admin_bordered bg-white p-4 text-center text-sm text-admin_text_secondary" role="alert" data-testid="sign-phone-expired">
          {state === "expired" ? texts.phoneExpired : texts.phoneError}
        </div>
        {again}
      </div>
    )

  return (
    <div className="flex flex-col gap-3" data-testid="sign-phone">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-admin_text">{texts.phoneTitle}</span>
        <span className="text-xs text-admin_text_secondary">{texts.phoneHint}</span>
      </div>
      <div className="flex justify-center rounded-md border border-admin_bordered bg-white p-3" data-testid="sign-phone-qr">
        {mobile.renderQR?.(url)}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-admin_text_secondary">{texts.phoneLink}</span>
        <div className="flex gap-2">
          <input readOnly value={url} onFocus={(e) => e.target.select()} className={twMerge(inputClass, "h-[34px] text-xs")} data-testid="sign-phone-link" />
          <Button variant="admin_secondary" className="h-[34px] shrink-0 text-xs" onClick={copy}>
            {copied ? texts.copied : texts.copyLink}
          </Button>
        </div>
      </div>
      {state === "signed" ? (
        again
      ) : (
        <div className="flex flex-col gap-0.5 text-xs text-admin_text_secondary">
          <span className="flex items-center gap-2 text-admin_text">
            <span className="w-3 h-3 rounded-full border-2 border-admin_primary border-t-transparent animate-spin" />
            {texts.phoneWaiting}
          </span>
          {session?.expiresAt && <span>{String(texts.phoneValidUntil).replace("%s", timeOf(session.expiresAt))}</span>}
        </div>
      )}
    </div>
  )
}

/** Whether the viewer is at least `md` wide (a phone signs by drawing, not with another phone). */
export function useWide() {
  const query = "(min-width: 768px)"
  const [wide, setWide] = useState(() => typeof window === "undefined" || !window.matchMedia || window.matchMedia(query).matches)
  useEffect(() => {
    if (!window.matchMedia) return
    const list = window.matchMedia(query)
    const change = () => setWide(list.matches)
    change()
    list.addEventListener?.("change", change)
    return () => list.removeEventListener?.("change", change)
  }, [])
  return wide
}

// ---- the panel ----

const MODES = ["draw", "type"]

export default function SignTools({ onSign, busy = false, error, defaultName = "", consentText, texts, mobile, className = "", "data-testid": testId = "sign-tools" }) {
  const words = { ...TEXTS, ...texts }
  const wide = useWide()
  const modes = mobile && wide ? [...MODES, "phone"] : MODES
  const [chosen, setMode] = useState("draw")
  // the Phone tab gone (the window narrowed to a phone's): drawing, until it is back
  const mode = modes.includes(chosen) ? chosen : "draw"
  const [color, setColor] = useState(COLORS[0].value)
  const [draft, setDraft] = useState(null)
  const [image, setImage] = useState(null)
  const [name, setName] = useState(defaultName)
  const [nameTouched, setNameTouched] = useState(false)
  const [agree, setAgree] = useState(false)
  // bumped by Clear: the pads made anew, empty
  const [fresh, setFresh] = useState(0)
  // the name field, focused once the signature is in and the signer's part has slid in
  const nameInput = useRef(null)
  const focusName = useRef(false)

  // the name the host knows, until the signer types one
  useEffect(() => {
    if (!nameTouched && defaultName) setName(defaultName)
  }, [defaultName, nameTouched])

  const switchMode = (next) => {
    setMode(next)
    setDraft(null)
  }

  const use = () => {
    if (!draft) return
    const png = signaturePng(draft)
    if (!png) return
    focusName.current = true
    setImage(png)
  }

  // a person's name: letters, spaces, . ' - only, first and last (aldoo-ra/Store/address-validation)
  const nameOk = !personNameError(name)
  const ready = !!image && nameOk && agree && !busy
  const sign = () => ready && onSign?.({ image, name: name.trim() })

  return (
    <div className={twMerge("flex flex-col gap-4 p-4", className)} data-testid={testId}>
      {/* a host that gives the panel its own heading (a dialog's title) passes an empty title */}
      {words.title && <div className="text-sm font-semibold text-admin_text">{words.title}</div>}

      {/* the signature: made, or seen with Change / Clear */}
      <div className="flex flex-col gap-2">
        <Label>{words.signature}</Label>
        {image && (
          <div className="flex flex-col gap-2" data-testid="sign-preview">
            <div className="relative w-full rounded-md border border-admin_bordered bg-white" style={{ aspectRatio: "3 / 1" }}>
              {/* held to the box, however tall the signature (one drawn on a phone is) */}
              <img src={image} alt={words.signature} className="absolute inset-3 w-[calc(100%-1.5rem)] h-[calc(100%-1.5rem)] object-contain" />
            </div>
            <div className="flex gap-2">
              <Button variant="admin_secondary" className="flex-1 h-[34px]" onClick={() => setImage(null)} disabled={busy}>
                {words.change}
              </Button>
              <Button
                variant="admin_secondary"
                className="flex-1 h-[34px]"
                onClick={() => {
                  setImage(null)
                  setDraft(null)
                  setFresh((n) => n + 1)
                }}
                disabled={busy}
              >
                {words.clear}
              </Button>
            </div>
          </div>
        )}
        {/* kept while the signature is previewed, so Change goes back to it as it was */}
        <div className={twMerge("flex flex-col gap-2", image && "hidden")} key={fresh}>
          <div className="flex gap-1 p-1 bg-admin_neutral_1 rounded-lg" role="tablist">
            {modes.map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                onClick={() => switchMode(m)}
                className={twMerge(
                  "flex-1 py-1.5 px-2 text-xs font-medium rounded-md flex items-center justify-center gap-1.5 transition-all",
                  mode === m ? "bg-admin_primary text-white shadow-sm" : "text-admin_text_secondary hover:text-admin_text"
                )}
                data-testid={`sign-mode-${m}`}
              >
                {m === "draw" && <PencilIcon className="w-3.5 h-3.5" />}
                {m === "type" && <span className="font-serif italic leading-none">Aa</span>}
                {m === "phone" && <DevicePhoneMobileIcon className="w-3.5 h-3.5" />}
                {words[m]}
              </button>
            ))}
          </div>

          {(mode === "draw" || mode === "type") && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-admin_text_secondary">{words.color}:</span>
              {COLORS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  title={words[c.id]}
                  aria-label={words[c.id]}
                  aria-pressed={color === c.value}
                  onClick={() => setColor(c.value)}
                  className={twMerge("w-6 h-6 rounded-full border-2 transition-transform", color === c.value ? "border-admin_primary scale-110" : "border-admin_bordered")}
                  style={{ backgroundColor: c.value }}
                />
              ))}
            </div>
          )}

          {mode === "draw" && <DrawPad color={color} onDraft={setDraft} texts={words} />}
          {mode === "type" && <TypePad color={color} defaultText={name} onDraft={setDraft} texts={words} />}
          {/* the phone's signature is the signature at once: seen with Change / Clear like any other */}
          {mode === "phone" && <PhonePad mobile={mobile} onImage={setImage} texts={words} />}

          {mode !== "phone" && (
            <Button variant="admin_secondary" className="w-full h-[34px]" disabled={!draft} onClick={use} data-testid="sign-use">
              {words.useSignature}
            </Button>
          )}
        </div>
      </div>

      {/* who signs and Sign: once there is a signature to sign with, not before */}
      <AnimatePresence initial={false}>
        {image && (
          <motion.div
            key="signer"
            initial={{ opacity: 0, height: 0, y: 8 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: 8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onAnimationComplete={() => {
              if (!focusName.current) return
              focusName.current = false
              nameInput.current?.focus()
            }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-4 pt-1">
          <label className="flex flex-col gap-1">
            <Label>{words.name} *</Label>
            <input
              value={name}
              onChange={(e) => (setName(e.target.value), setNameTouched(true))}
              onBlur={() => setNameTouched(true)}
              className={twMerge(inputClass, nameTouched && !nameOk && "border-admin_error")}
              maxLength={120}
              autoComplete="name"
          ref={nameInput}
              disabled={busy}
              data-testid="sign-name"
            />
            {nameTouched && !nameOk && <span className="text-[11px] text-admin_error">{words.nameShort}</span>}
          </label>

          <span data-testid="sign-consent">
            <Checkbox
              variant="admin_compact"
              isOn={agree}
              onChange={setAgree}
              disabled={busy}
              label={consentText || words.consent}
              labelClassName="text-sm font-normal"
              className="select-none items-start [&>div]:shrink-0"
            />
          </span>

          {error && (
            <div className="text-sm text-admin_error" role="alert">
              {error}
            </div>
          )}

          <Button variant="admin_primary" className="w-full" disabled={!ready} onClick={sign} data-testid="sign-submit">
            {busy ? words.signing : words.sign}
          </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
